import React from 'react';
import { Box, Chip, Typography, Button } from '@mui/material';
import { Close as RemoveIcon } from '@mui/icons-material';
import { neutral } from '_lex/_foundations/phxColors';

interface DTBSelectionChipsProps {
  dtbs: any[];
  selectedDtbs: string[];
  selectedCriteria: string[];
  setSelectedDtbs: (ids: string[]) => void;
  setSelectedCriteria: (criteria: string[]) => void;
  t: (key: string, fallback?: string) => string;
}

const DTBSelectionChips: React.FC<DTBSelectionChipsProps> = ({
  dtbs,
  selectedDtbs,
  selectedCriteria,
  setSelectedDtbs,
  setSelectedCriteria,
  t,
}) => {
  if (!selectedDtbs.length) return null;
  const selected = dtbs.filter((dtb) => selectedDtbs.includes(dtb.id));
  const statusMap: Record<string, string[]> = {};
  const flagMap: Record<string, string[]> = {};
  selected.forEach((dtb) => {
    // Status chips
    const status = dtb.status?.toUpperCase() || 'DRAFT';
    if (!statusMap[status]) statusMap[status] = [];
    statusMap[status].push(dtb.id);
    // Flag chips
    const differenceFlag = (dtb as any).differenceFlag;
    if (differenceFlag && differenceFlag !== 'NONE') {
      if (!flagMap[differenceFlag]) flagMap[differenceFlag] = [];
      flagMap[differenceFlag].push(dtb.id);
    }
  });
  const chips = [
    // Status chips
    ...Object.entries(statusMap).map(([status, ids]) => (
      <Chip
        key={`status-${status}`}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant='bodyMd'
              sx={{ fontWeight: 400 }}
            >
              {t('dtbs.packages.detail.statusChip', 'Status:')}{' '}
              {t(`dtbs.status.${status}`, status.charAt(0) + status.slice(1).toLowerCase())}
            </Typography>
          </Box>
        }
        onDelete={() => {
          setSelectedDtbs(selectedDtbs.filter((id: string) => !ids.includes(id)));
          // Remove from selectedCriteria if present
          setSelectedCriteria(selectedCriteria.filter((c: string) => c.toUpperCase() !== status));
        }}
        deleteIcon={<RemoveIcon />}
        size='small'
        sx={{
          justifyContent: 'space-between',
          gap: 2,
          height: '2rem',
          minHeight: '2rem',
          backgroundColor: neutral[25],
          border: `1px solid ${neutral[300]}`,
          '& .MuiChip-label': {
            lineHeight: '2rem',
            padding: '0 8px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        }}
      />
    )),
    // Flag chips
    ...Object.entries(flagMap).map(([flag, ids]) => (
      <Chip
        key={`flag-${flag}`}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant='bodyMd'
              sx={{ fontWeight: 400 }}
            >
              {t('dtbs.packages.detail.flagChip', 'Flag:')}{' '}
              {flag === 'CONTENT_MISSING'
                ? t('dtbs.packages.detail.flagChip.brokenReference', 'Broken Reference')
                : flag === 'CONTENT_DIFFERENCES'
                  ? t('dtbs.packages.detail.flagChip.differentContent', 'Different Content')
                  : flag === 'PENDING_UPDATES'
                    ? t('dtbs.packages.detail.flagChip.pendingUpdate', 'Pending')
                    : flag}
            </Typography>
          </Box>
        }
        onDelete={() => {
          setSelectedDtbs(selectedDtbs.filter((id: string) => !ids.includes(id)));
          // Remove from selectedCriteria if present
          setSelectedCriteria(
            selectedCriteria.filter(
              (c: string) =>
                !(
                  (c === 'BROKEN_REFERENCE' && flag === 'CONTENT_MISSING') ||
                  (c === 'DIFFERENT_CONTENT' && flag === 'CONTENT_DIFFERENCES') ||
                  (c === 'PENDING_UPDATE' && flag === 'PENDING_UPDATES')
                )
            )
          );
        }}
        deleteIcon={<RemoveIcon />}
        size='small'
        sx={{
          justifyContent: 'space-between',
          gap: 2,
          height: '2rem',
          minHeight: '2rem',
          backgroundColor: neutral[25],
          border: `1px solid ${neutral[300]}`,
          '& .MuiChip-label': {
            lineHeight: '2rem',
            padding: '0 8px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        }}
      />
    )),
  ];
  return (
    <Box
      sx={{
        background: neutral[25],
        px: 2,
        py: 4,
        borderRadius: 2,
        mt: 2,
        display: 'flex',
        gap: 2,
        flexWrap: 'wrap',
        alignItems: 'center',
        width: '100%',
      }}
    >
      {chips}
      {/* Clear all should always be last if present */}
      {chips.length > 1 && (
        <Box
          key='clear-all'
          role='button'
          tabIndex={0}
          onClick={() => {
            setSelectedDtbs([]);
            setSelectedCriteria([]);
          }}
          sx={{
            ml: 2,
            fontWeight: 400,
            color: 'primary.main',
            textDecoration: 'underline',
            cursor: 'pointer',
            outline: 'none',
            userSelect: 'none',
            display: 'inline',
          }}
        >
          {t('dtbs.packages.detail.clearAll')}
        </Box>
      )}
    </Box>
  );
};

export default DTBSelectionChips;
